import { isRouteRestorable } from '../../routing/routes';
import { Brand } from '../common/miscUtils.types';
import { storage } from '../localStorage';
import type { RootState } from '../redux';

/**
 * - It used to retrieve cached state from media storage.
 * - E.g. `asd-123_qwe-456`, where `asd-123` is fileId in media storage,
 * and `qwe-456` is an UUID generated on FES as state was being stored.
 * - UUID part is to make it extra unlikely for unwanted person to scan media storage
 * for state files as endpoint is not API-key protected nor the retrieval requires 2FA.
 */
export type CompositeFileId = Brand<string, 'CompositeFileId'>;

const _compositeFileIdUrlParamName = 'compositeFileId';

/**
 * - Saves data to localStorage for the sake of easier user session restoration
 * at arbitrary point in time.
 */
export const cacheReduxStateInLocalStorage = (cacheableState: Partial<RootState>) => {
  storage.restore.cachedReduxState = JSON.stringify(cacheableState);
  storage.restore.cachedPathname = window.location.pathname;
};

export const setupRestore = (getCacheableState: () => Partial<RootState>) => {
  window.addEventListener('unload', () => {
    if (isRouteRestorable()) {
      const cacheableState = getCacheableState();
      cacheReduxStateInLocalStorage(cacheableState);
    }
  });
};

const getCachedReduxState = () => {
  let cachedState: Partial<RootState> | undefined;

  try {
    cachedState = JSON.parse(storage.restore.cachedReduxState!) as Partial<RootState>;
  } catch {
    return undefined;
  }

  return cachedState;
};

let _cachedReduxStateWasRestored = false;

export const getInitialReduxStoreState = () => {
  if (!isRouteRestorable()) return {};

  const cachedReduxState = getCachedReduxState();

  const { cachedPathname } = storage.restore;
  const isCachedStateForCurrentRoute = window.location.pathname === cachedPathname;

  delete storage.restore.cachedReduxState;
  delete storage.restore.cachedPathname;

  if (cachedReduxState && isCachedStateForCurrentRoute) {
    _cachedReduxStateWasRestored = true;
    return cachedReduxState;
  }

  return {};
};

/**
 * - Whether current route is categorized as restorable
 * and it's actually feasible to do valid restoration.
 */
export const canLoadAppOnRestorableRoute = () => {
  return isRouteRestorable() && _cachedReduxStateWasRestored;
};
